import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import Icon from '../Icon/icon.tsx';

export interface ProgressBarProps {
  size?: 'small' | 'large';
  state?: 'in-progress' | 'error' | 'success' | 'indeterminate';
  label?: string;
  helperMessage?: string;
  className?: string;
  value?: number;
  maxValue?: number;
  hideIcon?: boolean;
}

const ProgressBarVariants = cva('ds-overflow-hidden ds-bg-neutrals-light', {
  variants: {
    size: {
      small: ['ds-h-2', 'ds-rounded-sm'],
      large: ['ds-h-4', 'ds-rounded']
    }
  }
});

const ProgressVariants = cva('', {
  variants: {
    size: {
      small: ['ds-h-2', 'ds-rounded-sm'],
      large: ['ds-h-4', 'ds-rounded']
    },
    state: {
      'in-progress': ['ds-bg-acuity-blue'],
      error: ['ds-bg-danger-red-dark'],
      success: ['ds-bg-success-green'],
      indeterminate: ['ds-bg-acuity-blue', 'ds-animate-progress']
    }
  }
});

const StatusIndicatorVariants = cva(
  'ds-w-[3rem] ds-justify-end ds-flex ds-items-center ds-text-neutrals-dark',
  {
    variants: {
      size: {
        small: 'ds-text-sm',
        large: ''
      }
    }
  }
);

const HelperMessageVariants = cva('ds-text-sm', {
  variants: {
    state: {
      error: 'ds-text-danger-red',
      success: 'ds-text-neutrals-dark',
      indeterminate: 'ds-text-neutrals-dark',
      'in-progress': 'ds-text-neutrals-dark'
    }
  }
});

const ProgressBar = ({
  size = 'large',
  label = '',
  helperMessage = '',
  className = '',
  state = 'indeterminate',
  maxValue = 100,
  value = 0,
  hideIcon = false,
  ...props
}: ProgressBarProps) => {
  const getPercentValue = () => {
    let percent = Math.round((value / maxValue) * 100);

    if (percent < 0) {
      percent = 0;
    } else if (percent > 100) {
      percent = 100;
    }

    return state !== 'in-progress' ? '100%' : `${percent}%`;
  };

  const getStatusIcon = () => {
    if (hideIcon) {
      return null;
    }

    switch (state) {
      case 'in-progress':
        return getPercentValue();
      case 'error':
        return (
          <Icon
            name={'error'}
            size={size === 'small' ? 'small' : 'medium'}
            className="ds-float-right ds-text-danger-red-dark"
            role="graphics-symbol"
            filled={true}
          />
        );
      case 'success':
        return (
          <Icon
            name={'checkCircle'}
            size={size === 'small' ? 'small' : 'medium'}
            className="ds-float-right ds-text-success-green-dark"
            role="graphics-symbol"
            filled={true}
          />
        );
      case 'indeterminate':
      default:
        return null;
    }
  };

  const getEllipses = () => {
    if (state === 'in-progress' || state === 'indeterminate') {
      return (
        <div className="ds-ml-1 ds-mt-2 ds-flex ds-w-[15px] ds-items-center ds-space-x-[2px]">
          <div className="ds-h-[3px] ds-w-[3px] ds-animate-blink ds-rounded-full ds-bg-neutrals-black [animation-delay:-0.2s]"></div>
          <div className="ds-h-[3px] ds-w-[3px] ds-animate-blink ds-rounded-full ds-bg-neutrals-black [animation-delay:-0.15s]"></div>
          <div className="ds-h-[3px] ds-w-[3px] ds-animate-blink ds-rounded-full ds-bg-neutrals-black"></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={twMerge('preflight ds-min-w-[4.875rem]', className)} {...props}>
      <div className="ds-flex ds-h-5 ds-items-center ds-justify-between">
        <div className="progress-bar-label ds-flex ds-max-w-[calc(100%-3rem)] ds-overflow-hidden">
          <p className="ds-overflow-hidden ds-text-nowrap">{label}</p>
          {getEllipses()}
        </div>
        <div aria-hidden={true} className={StatusIndicatorVariants({ size })}>
          {getStatusIcon()}
        </div>
      </div>
      <div
        role="progressbar"
        className="ds-mb-1 ds-mt-1"
        aria-label={label}
        aria-valuemin={0}
        aria-valuenow={value}
        aria-valuemax={maxValue}
        aria-valuetext={getPercentValue()}
      >
        <div className={ProgressBarVariants({ size })}>
          <div
            style={{ width: getPercentValue() }}
            className={ProgressVariants({ size, state })}
          />
        </div>
      </div>
      <p className={HelperMessageVariants({ state })}>{helperMessage}</p>
    </div>
  );
};

export default ProgressBar;
